import useHandleWorkCenter from "../../../Hooks/useHandleData/useHandleWorkCenter";
import useHandleHoursByUserId from "../../../Hooks/useHandleData/useHandleHoursByUserId";
import { checkRoles } from "../../../Utils/helper";
import { Button } from "devextreme-react/button";
import useHandleSchedule from "../../../Hooks/useHandleData/useHandleSchedule";

const days = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export default function AppointmentTooltip({ e, userId, handleUpdateForm }) {
  const { workCenters } = useHandleWorkCenter();
  const { handleDelHours } = useHandleHoursByUserId(userId);

  const tooltipData = e?.data?.appointmentData;
  const { scheduleData = [] } = useHandleSchedule(userId);

  // Build per-day array of shifts from scheduleData (Sun..Sat indexes match `days` array).
  // Keeps both raw times (for minute precision) and derived hour strings (legacy usage).
  const scheduleObject = days.reduce((acc, day, index) => {
    const shifts = scheduleData?.[index]?.employeeScheduleTime ?? [];

    acc[day] = shifts.map((s, i) => ({
      startTime: s?.startTime ?? "",
      endTime: s?.endTime ?? "",
      startHour: s?.startTime ? s.startTime.substring(11, 13) : "",
      endHour: s?.endTime ? s.endTime.substring(11, 13) : "",
      idx: i,
    }));

    return acc;
  }, {});

  if (tooltipData?.colorId === 1) {
    const onAppointmentDeleted = async (e) => {
      e.event.stopPropagation();

      await handleDelHours(tooltipData.id);
    };
    const onAppointmentEdit = async (e) => {
      e.event.stopPropagation();

      handleUpdateForm(tooltipData);
    };

    const location = workCenters?.find(
      (x) => x.value === tooltipData?.locationId ?? null
    );
    const startDate = new Date(tooltipData?.startDate).toLocaleDateString(
      "en-US",
      { month: "long", day: "numeric" }
    );

    return (
      <div
        className={"dx-tooltip-appointment-item"}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={"dx-tooltip-appointment-item-content"}>
          <div className={"dx-tooltip-appointment-item-content"}>
            <div className="dx-tooltip-appointment-item-content-subject">
              {tooltipData?.text}
            </div>
            <div className="dx-tooltip-appointment-item-content-date">
              {location?.label}
            </div>
            <div className="dx-tooltip-appointment-item-content-date">
              {startDate}: {tooltipData?.hours} hours
            </div>
          </div>
        </div>
        <div className={"dx-tooltip-appointment-item-edit-button-container"}>
          <Button
            className={"dx-tooltip-appointment-item-edit-button"}
            icon="edit"
            stylingMode="text"
            onClick={onAppointmentEdit}
          ></Button>
        </div>
        <div className={"dx-tooltip-appointment-item-delete-button-container"}>
          <Button
            className={"dx-tooltip-appointment-item-delete-button"}
            icon="trash"
            stylingMode="text"
            onClick={onAppointmentDeleted}
          ></Button>
        </div>
      </div>
    );
  } else if (tooltipData?.colorId === 2 || tooltipData?.colorId === 3) {
    const comments = tooltipData?.comments;

    const newComment =
      Array.isArray(comments) && comments.length > 0
        ? comments.map((comment, index) => {
            let style = { backgroundColor: "#FFEFCC" }; // neutral default
            const raw = comment?.comments ?? "";
            const parts = raw.split(" : ");
            const date = parts[0] ?? "";
            const name = parts[1] ?? "";
            let remark = parts.slice(2).join(" : ") || "";

            if (remark === "Approved") {
              style.backgroundColor = "#D7FFF2";
            } else if (remark === "Denied") {
              style.backgroundColor = "#FFA4C2";
            } else if (index === 0) {
              remark = "Comment: " + remark;
            } else {
              remark = "Comment: " + remark;
            }

            return (
              <div className="d-flex justify-content-center">
                <div
                  id="comment-box"
                  style={style}
                  key={comment.id}
                  className="black"
                >
                  <div id="comment-info">
                    <div id="comment-name">{name}</div>
                    <div id="comment-date">{date}</div>
                  </div>

                  <div id="comment-remark">{remark}</div>
                </div>
              </div>
            );
          })
        : null;

    // --- Business hours calculation rules ---
    // - Weekends excluded
    // - If no schedule: middle days = 8h; first/last days = overlap with 8–5 fallback
    // - If schedule present: middle days = sum of all shifts; first/last = overlap only
    const isWeekend = (d) => [0, 6].includes(d.getDay()); // Sun=0, Sat=6
    const dayKey = (d) => days[d.getDay()];

    const buildShiftIntervalsForDate = (date, dayShifts = []) => {
      // Build precise [start,end] Date intervals using the raw startTime/endTime (keeps minutes!)
      return dayShifts
        .filter((s) => s?.startTime && s?.endTime)
        .map((s) => {
          const srcStart = new Date(s.startTime);
          const srcEnd = new Date(s.endTime);

          const start = new Date(date);
          start.setHours(srcStart.getHours(), srcStart.getMinutes(), 0, 0);

          const end = new Date(date);
          end.setHours(srcEnd.getHours(), srcEnd.getMinutes(), 0, 0);

          return end > start ? [start, end] : null;
        })
        .filter(Boolean);
    };

    const sumIntervalHours = (intervals) =>
      intervals.reduce((sum, [a, b]) => sum + (b - a) / (1000 * 60 * 60), 0);

    const sumOverlapHours = (intervals, windowStart, windowEnd) =>
      intervals.reduce((sum, [a, b]) => {
        const s = a > windowStart ? a : windowStart;
        const e = b < windowEnd ? b : windowEnd;
        const diff = (e - s) / (1000 * 60 * 60);
        return diff > 0 ? sum + diff : sum;
      }, 0);

    // --- Calculate business hours ---
    const start = new Date(tooltipData.startDate);
    const end = new Date(tooltipData.endDate);

    let businessHours = 0;
    let cursor = new Date(start);

    // normalize cursor to the start of the day (we’ll handle windows per-day)
    cursor.setHours(0, 0, 0, 0);

    while (cursor <= end) {
      if (!isWeekend(cursor)) {
        const firstDay = cursor.toDateString() === start.toDateString();
        const lastDay = cursor.toDateString() === end.toDateString();
        const key = dayKey(cursor);
        const dayShifts = scheduleObject?.[key] ?? [];

        const dayStart = new Date(cursor);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(cursor);
        dayEnd.setHours(23, 59, 59, 999);

        const windowStart = firstDay ? start : dayStart;
        const windowEnd = lastDay ? end : dayEnd;

        //compute today's hours, then clamp to 8
        let dayHours = 0;

        if (dayShifts.length > 0) {
          // Scheduled day
          const intervals = buildShiftIntervalsForDate(cursor, dayShifts);
          if (!firstDay && !lastDay) {
            dayHours = sumIntervalHours(intervals);
          } else {
            dayHours = sumOverlapHours(intervals, windowStart, windowEnd);
          }
        } else {
          // No schedule → fallback
          if (!firstDay && !lastDay) {
            dayHours = 8;
          } else {
            const fallbackStart = new Date(cursor);
            fallbackStart.setHours(8, 0, 0, 0);
            const fallbackEnd = new Date(cursor);
            fallbackEnd.setHours(17, 0, 0, 0);
            dayHours = sumOverlapHours(
              [[fallbackStart, fallbackEnd]],
              windowStart,
              windowEnd
            );
          }
        }

        businessHours += Math.min(8, dayHours); //hard cap per day
      }

      // move to next day
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(0, 0, 0, 0);
    }

    return (
      <div
        className="dx-tooltip-appointment-item"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dx-tooltip-appointment-item-content">
          {/* Business hours block */}
          <div
            style={{
              width: "100%",
              backgroundColor: "rgb(102, 102, 102,0.5)",
              padding: "8px 12px",
              borderRadius: "6px",
              fontWeight: "bold",
              fontSize: "15px",
              textAlign: "center",
              marginBottom: "4px",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            {Number(businessHours.toFixed(2))} Business Hours
          </div>

          {/* Date range display */}
          <div
            style={{
              fontSize: "13px",
              color: "rgb(187, 187, 187)",
              textAlign: "center",
              marginBottom: "10px",
            }}
          >
            {`${new Date(tooltipData.startDate).toLocaleString("en-US", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })} — ${new Date(tooltipData.endDate).toLocaleString("en-US", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}`}
          </div>

          {/* Comments */}
          {newComment}
        </div>
      </div>
    );
  }
}
